import type { ElsewhereCard } from 'shared/api'
import { PageFrame } from 'shared/components/page-frame'
import { Hint } from 'shared/components/section'
import { Badge } from 'shared/components/ui/badge'
import { useTranslate } from 'shared/lib/i18n'
import { elsewhereWords, whereNowWords, type WhereNowAnswer } from './lookup-words'
import { Fact, Facts } from 'shared/components/facts'
import { BackButton } from './parcel-parts'

/**
 * Where a Parcel or a Container is (ticket 48; spec §22, user stories 104, 105, 126):
 * the chain of its holders as the server read it — «у водителя Жасур · мешок M-… ·
 * рейс R-…», «в хабе HUB-TAS · мешок M-… · коробка …» — and, to a Point operator
 * about another Point's Parcel, only «в пункте X», «в пути» or «не в сети». The words
 * are the screen's; the place is the server's answer, never re-derived here.
 */

/** «Где сейчас» on a card; `ended` — the Parcel's way is over; otherwise still with its partner. */
export function WhereNow({ where, ended }: { where: WhereNowAnswer | null; ended: boolean }) {
	const { t } = useTranslate()
	return (
		<span data-testid='where-now'>
			{where ? whereNowWords(t, where) : t(ended ? 'where.nowhereEnded' : 'where.nowhereExpected')}
		</span>
	)
}

/**
 * The card of another Point's Parcel, to a Point operator: its code and where it is —
 * nothing of its Recipient, amount, history or payment (the server sends none of it).
 */
export function ElsewhereParcel({ parcel, onBack }: { parcel: ElsewhereCard; onBack?: () => void }) {
	const { t } = useTranslate()
	return (
		<PageFrame
			back={onBack && <BackButton onBack={onBack} />}
			heading={
				<>
					<span data-testid='parcel-elsewhere-code'>{parcel.trackingCode}</span>
					<Badge variant='outline'>{t('where.elsewhereTitle')}</Badge>
					<Hint>{t('where.elsewhereHint')}</Hint>
				</>
			}
		>
			<div data-testid='parcel-elsewhere'>
				<Facts>
					<Fact label={t('where.title')}>
						<span className='text-base font-medium' data-testid='parcel-elsewhere-where'>
							{elsewhereWords(t, parcel)}
						</span>
					</Fact>
				</Facts>
			</div>
		</PageFrame>
	)
}
